/**
 * Symbol used for storing the resolved value.
 */
const VALUE: unique symbol = Symbol('Cached Promise Value')

export const ValueRetrievablePromise = {
  /**
   * Whether the given promise is a {@link ValueRetrievablePromise}.
   * @param value
   */
  isRetrievable: <T>(
    value: PromiseLike<T>,
  ): value is ValueRetrievablePromise<T> => VALUE in value,

  /**
   * Gets the resolved value of the promise.
   * @param promise
   */
  getResolved: <T>(promise: ValueRetrievablePromise<T>): T => promise[VALUE],

  /**
   * Creates a promise where the resolved value of {@link promise} can be
   * extracted without chaining.
   *
   * @param promise
   */
  from: <T>(promise: PromiseLike<T>): PromiseLike<T> => {
    const retrievable: PromiseLike<T> = {
      then<TResult1 = T, TResult2 = never>(
        onFulfilled?: ((value: T) => PromiseLike<TResult1> | TResult1) | null,
        onRejected?:
          | ((reason: unknown) => PromiseLike<TResult2> | TResult2)
          | null,
      ): PromiseLike<TResult1 | TResult2> {
        return promise.then((value) => {
          ;(retrievable as ValueRetrievablePromise<T>)[VALUE] = value
          if (onFulfilled) {
            return onFulfilled(value)
          }
          return value
        }, onRejected) as PromiseLike<TResult1 | TResult2>
      },
    }

    return retrievable
  },

  /**
   * Calls {@link fn} every time the returned promise is chained.
   *
   * @param fn
   */
  defer: <T>(fn: () => PromiseLike<T>): PromiseLike<T> => {
    const retrievable: PromiseLike<T> = {
      then<TResult1 = T, TResult2 = never>(
        onFulfilled?: ((value: T) => PromiseLike<TResult1> | TResult1) | null,
        onRejected?:
          | ((reason: unknown) => PromiseLike<TResult2> | TResult2)
          | null,
      ): PromiseLike<TResult1 | TResult2> {
        return fn().then((value) => {
          ;(retrievable as ValueRetrievablePromise<T>)[VALUE] = value
          if (onFulfilled) {
            return onFulfilled(value)
          }
          return value
        }, onRejected) as PromiseLike<TResult1 | TResult2>
      },
    }

    return retrievable
  },

  /**
   * Creates a promise that calls the fulfilled callback immediately on chaining.
   *
   * @param resolved
   */
  fromValue: <T>(resolved: T): ValueRetrievablePromise<T> => {
    return {
      [VALUE]: resolved,
      then<TResult1, TResult2>(
        onFulfilled: (<T>(value: T) => PromiseLike<TResult1> | TResult1) | null,
      ): PromiseLike<TResult1 | TResult2> {
        if (onFulfilled) {
          // Calling `onFulfilled` synchronously
          // here is what makes this function special.
          return Promise.resolve(onFulfilled(resolved))
        }

        return Promise.resolve(resolved as TResult1 | TResult2)
      },
    }
  },

  /**
   * Creates a promise-like that resolves to an object where each property is the
   * resolved value of the corresponding property in {@link props}.
   *
   * If all properties in {@link props} are {@link ValueRetrievablePromise}s
   * which have already been resolved, the returned promise will resolve
   * immediately with an object containing the resolved values. The returned promise
   * will itself be a {@link ValueRetrievablePromise}.
   *
   * Otherwise, the returned promise will resolve when all properties in
   * {@link props} have been resolved; this will also be a {@link ValueRetrievablePromise}.
   *
   * @param props
   */
  props: <O extends Record<string, PromiseLike<unknown>>>(
    props: O,
  ): PromiseLike<{
    [K in keyof O]: O[K] extends PromiseLike<infer T> ? Awaited<T> : never
  }> => {
    const resolved = new Map<string, unknown>()
    let pending:
      | {
          key: string
          promise: PromiseLike<unknown>
        }[]
      | undefined
    const keys: string[] = []
    for (const [key, value] of Object.entries(props)) {
      keys.push(key)
      if (ValueRetrievablePromise.isRetrievable(value)) {
        resolved.set(key, ValueRetrievablePromise.getResolved(value))
      } else {
        pending ??= []
        pending.push({ key, promise: value })
      }
    }

    if (!pending || pending.length === 0) {
      return ValueRetrievablePromise.fromValue(finish())
    }

    return ValueRetrievablePromise.from(
      Promise.all(
        pending.map((p) =>
          p.promise.then((value) => {
            resolved.set(p.key, value)
          }),
        ),
      ).then(finish),
    )

    function finish() {
      const result: Record<string, unknown> = {}
      for (const key of keys) {
        result[key] = resolved.get(key)
      }

      return result as {
        [K in keyof O]: O[K] extends PromiseLike<infer T> ? Awaited<T> : never
      }
    }
  },
} as const

/**
 * A promise where the resolved value can be extracted without
 * creating a new promise.
 */
export interface ValueRetrievablePromise<T> extends PromiseLike<T> {
  [VALUE]: T
}
